/**
 * JSON-RPC 2.0 transport over the VS Code webview `postMessage` channel.
 *
 * App-specific method-name constants (e.g. `eval_log`, `get_scan`) stay local
 * to each app; only the wire-format machinery lives here so Inspect and Scout
 * share one implementation.
 */

import { JsonValue } from "./json-value";
import { VSCodeApi } from "./vscode";

// This is a structured-clone channel (postMessage), not a JSON wire, so params
// and results aren't restricted to JsonValue: callers legitimately pass
// `undefined` positional args and receive binary (e.g. Uint8Array) results.
// Typing them loosely keeps the transport honest; payload shapes are enforced
// by each caller (e.g. createJsonRpcFetch validates its HttpProxyResponse).
export type JsonRpcParams = readonly unknown[] | Record<string, unknown>;

// This isn't strictly correct. The data field is spec'ed to be JsonValue, but
// since we're in control of the server, it's fine.
type JsonRpcErrorData = { description?: string } & { [key: string]: JsonValue };

export type JsonRpcClient = (
  method: string,
  params?: JsonRpcParams
) => Promise<unknown>;

interface JsonRpcMessage {
  jsonrpc: string;
  id: number;
}

interface JsonRpcRequest extends JsonRpcMessage {
  method: string;
  params?: JsonRpcParams;
}

interface JsonRpcResponse extends JsonRpcMessage {
  result?: unknown;
  error?: JsonRpcError;
}

interface JsonRpcError {
  code: number;
  message: string;
  data?: JsonRpcErrorData;
}

interface RequestHandlers {
  resolve: (value: unknown) => void;
  reject: (error: JsonRpcError) => void;
}

// PostMessageTarget uses `unknown` because it's at the DOM boundary where
// MessageEvent.data is untyped. The JSON-RPC layer validates incoming data.
interface PostMessageTarget {
  postMessage: (data: unknown) => void;
  onMessage: (handler: (data: unknown) => void) => () => void;
}

export const kJsonRpcParseError = -32700;
export const kJsonRpcInvalidRequest = -32600;
export const kJsonRpcMethodNotFound = -32601;
export const kJsonRpcInvalidParams = -32602;
export const kJsonRpcInternalError = -32603;
export const kJsonRpcVersion = "2.0";

export function webViewJsonRpcClient(vscode: VSCodeApi): JsonRpcClient {
  const target: PostMessageTarget = {
    postMessage: (data: unknown) => {
      vscode.postMessage(data);
    },
    onMessage: (handler: (data: unknown) => void) => {
      const onMessage = (ev: MessageEvent) => {
        handler(ev.data);
      };
      window.addEventListener("message", onMessage);
      return () => {
        window.removeEventListener("message", onMessage);
      };
    },
  };
  return jsonRpcPostMessageRequestTransport(target).request;
}

const toErrorData = (data: unknown): JsonRpcErrorData =>
  typeof data === "string"
    ? { description: data }
    : typeof data === "object" && data !== null
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- wire boundary: a peer's error payload, structurally unverifiable short of walking it
        (data as JsonRpcErrorData)
      : { description: JSON.stringify(data) };

export function jsonRpcError(
  message: string,
  data?: unknown,
  code?: number
): JsonRpcError {
  return {
    code: code || -3200,
    message,
    data: data !== undefined ? toErrorData(data) : undefined,
  };
}

export function asJsonRpcError(error: unknown): JsonRpcError {
  if (typeof error === "object" && error !== null) {
    const err = error as { message?: string; data?: unknown; code?: number };
    if (typeof err.message === "string") {
      return jsonRpcError(err.message, err.data, err.code);
    }
  }
  return jsonRpcError(String(error));
}

export function jsonRpcPostMessageRequestTransport(target: PostMessageTarget) {
  const requests = new Map<number, RequestHandlers>();
  const disconnect = target.onMessage((ev: unknown) => {
    const response = asJsonRpcResponse(ev);
    if (response) {
      const request = requests.get(response.id);
      if (request) {
        requests.delete(response.id);
        if (response.error) {
          request.reject(response.error);
        } else {
          request.resolve(response.result ?? null);
        }
      }
    }
  });

  return {
    request: (method: string, params?: JsonRpcParams): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        const requestId = Math.floor(Math.random() * 1e6);
        requests.set(requestId, { resolve, reject });
        const request: JsonRpcRequest = {
          jsonrpc: kJsonRpcVersion,
          id: requestId,
          method,
          params,
        };
        target.postMessage(request);
      });
    },
    disconnect,
  };
}

export function jsonRpcPostMessageServer(
  target: PostMessageTarget,
  methods:
    | { [key: string]: (params: unknown) => Promise<unknown> }
    | ((name: string) => ((params: unknown) => Promise<unknown>) | undefined)
): () => void {
  const lookupMethod =
    typeof methods === "function" ? methods : (name: string) => methods[name];

  return target.onMessage((data: unknown) => {
    const request = asJsonRpcRequest(data);
    if (request) {
      const method = lookupMethod(request.method);
      if (!method) {
        target.postMessage(methodNotFoundResponse(request));
        return;
      }

      method(request.params || [])
        .then((value) => {
          target.postMessage(jsonRpcResponse(request, value));
        })
        .catch((error: unknown) => {
          target.postMessage({
            jsonrpc: request.jsonrpc,
            id: request.id,
            error: asJsonRpcError(error),
          });
        });
    }
  });
}

function isJsonRpcMessage(message: unknown): message is JsonRpcMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "jsonrpc" in message &&
    "id" in message
  );
}

function isJsonRpcRequest(message: JsonRpcMessage): message is JsonRpcRequest {
  return "method" in message;
}

function asJsonRpcMessage(data: unknown): JsonRpcMessage | null {
  if (isJsonRpcMessage(data) && data.jsonrpc === kJsonRpcVersion) {
    return data;
  }
  return null;
}

function asJsonRpcRequest(data: unknown): JsonRpcRequest | null {
  const message = asJsonRpcMessage(data);
  if (message && isJsonRpcRequest(message)) {
    return message;
  }
  return null;
}

function asJsonRpcResponse(data: unknown): JsonRpcResponse | null {
  const message = asJsonRpcMessage(data);
  if (message) {
    return message;
  }
  return null;
}

function jsonRpcResponse(
  request: JsonRpcRequest,
  result: unknown
): JsonRpcResponse {
  return {
    jsonrpc: request.jsonrpc,
    id: request.id,
    result,
  };
}

function jsonRpcErrorResponse(
  request: JsonRpcRequest,
  code: number,
  message: string
): JsonRpcResponse {
  return {
    jsonrpc: request.jsonrpc,
    id: request.id,
    error: jsonRpcError(message, undefined, code),
  };
}

function methodNotFoundResponse(request: JsonRpcRequest): JsonRpcResponse {
  return jsonRpcErrorResponse(
    request,
    kJsonRpcMethodNotFound,
    `Method '${request.method}' not found.`
  );
}
